import React, { useState, useEffect, useCallback } from "react";
import { FileTextOutlined, DeleteOutlined, PlayCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import { formatCurrency } from "../../../utils/formatters";
import { readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { parseLocalizedName } from "../../../utils/localization";
import { apiClient } from "../../../shared/api/AllApi";
import { confirmDanger } from "../../../shared/ui/confirm";
import usePermissions from "../../../hooks/usePermissions";

export const saveDraft = async ({ cart, selectedCustomer, paymentMethod, taxed = true }) => {
  const res = await apiClient.post("/pos/drafts", {
    customer_id: selectedCustomer || null,
    payment_method: paymentMethod || "cash",
    taxed: taxed !== false,
    cart_data: cart,
  });
  return res.data;
};

export const deleteDraft = async (id) => {
  await apiClient.delete(`/pos/drafts/${id}`, { data: { reason: "Draft discarded by user" } });
};

export const loadDrafts = async () => {
  try {
    const res = await apiClient.get("/pos/drafts");
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
};

const todayStr = () => new Date().toISOString().slice(0, 10);

const PosSubDrafts = ({ onBack, onResume }) => {
  const { t, i18n } = useTranslation();
  const { hasPermission } = usePermissions();
  const canDelete = hasPermission("pos.delete");
  const currency = readCachedSystemConfig()?.currency_code || "IQD";
  const [allDrafts, setAllDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const fetchDrafts = useCallback(async () => {
    setLoading(true);
    try {
      const list = await loadDrafts();
      setAllDrafts(list);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDrafts(); }, [fetchDrafts]);

  const today = todayStr();
  const drafts = showAll
    ? allDrafts
    : allDrafts.filter((d) => d.created_at && d.created_at.slice(0, 10) === today);

  const handleDelete = (id) => {
    confirmDanger({
      title: t("posDeleteDraftTitle", "Delete this draft?"),
      content: t("posDeleteWarning", "This action cannot be undone."),
      okText: t("delete", "Delete"),
      cancelText: t("cancel", "Cancel"),
      onOk: async () => {
        try {
          await deleteDraft(id);
          setAllDrafts((prev) => prev.filter((d) => d.id !== id));
        } catch {
          message.error(t("posDraftsDeleteFailed", "Failed to delete draft"));
        }
      },
    });
  };

  const itemCount = (draft) =>
    (draft.order_products || []).reduce((s, op) => s + parseFloat(op.quantity || 0), 0);

  const handleResume = (draft) => {
    const lang = i18n.language || "en";
    const cart = (draft.order_products || []).map((op) => ({
      id: op.product_id,
      product_id: op.product_id,
      name: parseLocalizedName(op.product?.name, lang, "--"),
      price: parseFloat(op.single_price) || null,
      qty: op.quantity ? parseFloat(op.quantity) : null,
      weight: op.weight ? parseFloat(op.weight) : null,
      unitId: null,
      vendorId: op.vendor_id || null,
    }));
    onResume({
      id: draft.id,
      cart,
      selectedCustomer: draft.customer_id || null,
      paymentMethod: draft.payment_method || "cash",
      taxed: draft.is_taxed !== false && draft.is_taxed !== 0,
    });
  };

  const todayCount = allDrafts.filter((d) => d.created_at && d.created_at.slice(0, 10) === today).length;

  return (
    <div className="pos-sub-view">
      <div className="pos-sub-header">
        <div className="pos-sub-header-left">
          <div className="pos-sub-icon"><FileTextOutlined /></div>
          <h2 className="pos-sub-title">{t("posDraftsTitle", "Draft Orders")}</h2>
          <span className="pos-sub-tab-badge" style={{ fontSize: 12, padding: "2px 10px" }}>{drafts.length}</span>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            style={{
              padding: "5px 14px", borderRadius: 8, border: "1.5px solid #e5e7eb",
              background: showAll ? "#0f172a" : "#fff", color: showAll ? "#fff" : "#374151",
              fontWeight: 600, fontSize: 12, cursor: "pointer",
            }}
          >
            {showAll ? `${t("all", "All")} (${allDrafts.length})` : `${t("today", "Today")} (${todayCount})`}
          </button>
          <button type="button" className="pos-sub-back" onClick={onBack}>{t("posBack", "Back")}</button>
        </div>
      </div>

      <div className="pos-sub-list">
        {loading ? (
          <div className="pos-sub-empty"><div className="pos-sub-empty-sub">{t("loading", "Loading...")}</div></div>
        ) : drafts.length === 0 ? (
          <div className="pos-sub-empty">
            <div className="pos-sub-empty-icon"><FileTextOutlined /></div>
            <div className="pos-sub-empty-title">{showAll ? t("posDraftsEmpty", "No saved drafts") : t("posDraftsNoneToday", "No drafts today")}</div>
            <div className="pos-sub-empty-sub">
              {!showAll && allDrafts.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowAll(true)}
                  style={{ color: "#2563eb", background: "none", border: "none", cursor: "pointer", fontSize: 13 }}
                >
                  {t("showAll", "Show all")} ({allDrafts.length})
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="pos-orders-list">
            {drafts.map((draft) => (
              <div key={draft.id} className="pos-orders-row" style={{ cursor: "default" }}>
                <div className="pos-orders-row-left">
                  <div className="pos-orders-id">
                    <FileTextOutlined style={{ fontSize: 13, marginRight: 4 }} />
                    {draft.customer?.name || t("posTerminalDraft", "Draft")}
                  </div>
                  <div className="pos-orders-meta">
                    <span className="pos-orders-date">{new Date(draft.created_at).toLocaleString()}</span>
                    <span style={{ fontSize: 11, color: "#6b7280" }}>
                      {itemCount(draft)} {t("posDraftsItems", "items")} · {draft.payment_method === "cash" ? t("posDraftsCash", "Cash") : t("posDraftsCredit", "Credit")}
                    </span>
                  </div>
                </div>
                <div className="pos-orders-row-right" style={{ gap: 8 }}>
                  <div className="pos-orders-total">{formatCurrency(draft.total_price, currency)}</div>
                  <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                    {canDelete && <button
                      type="button"
                      className="pos-sub-add-btn"
                      style={{ height: 32, fontSize: 12, padding: "0 10px", borderRadius: 7 }}
                      onClick={() => handleResume(draft)}
                    >
                      <PlayCircleOutlined /> {t("posDraftsResume", "Resume")}
                    </button>}
                    <button
                      type="button"
                      className="pos-sub-delete-btn"
                      style={{ height: 32, width: 32, borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center" }}
                      onClick={() => handleDelete(draft.id)}
                    >
                      <DeleteOutlined />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PosSubDrafts;
